import { Button, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { plural, t } from '@lingui/core/macro';
import { TRPCClientError } from '@trpc/client';

export function CoursePublishButton({ courseId }: { courseId: string }) {
  const [, query] = trpcAdmin.courses.byId.useSuspenseQuery(courseId);
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const publish = trpcAdmin.courses.publish.useMutation({
    onSuccess: (course) => {
      // Wake the coverage surfaces that read listByCourse (the toolbar pill and
      // the units-list badge): the fan-out committed its PENDING rows before this
      // resolved, so the refetch starts their poll from real progress.
      void utils.contentTranslations.listByCourse.invalidate({ courseId });
      void query.refetch();

      const kickoff = course.translationKickoff;
      const enqueued = kickoff?.translationsEnqueued ?? 0;
      const failed = kickoff?.translationsFailedToEnqueue ?? 0;

      if (failed > 0) {
        toast({
          title: t`Course published`,
          description: t`Translating ${enqueued} translations, ${failed} couldn't be started.`,
          status: 'warning',
          isClosable: true,
          duration: 7000,
        });
        return;
      }

      if (enqueued > 0) {
        toast({
          title: t`Course published`,
          description: plural(enqueued, {
            one: 'Translating # translation. Track progress in the toolbar.',
            other: 'Translating # translations. Track progress in the toolbar.',
          }),
          status: 'success',
          isClosable: true,
          duration: 5000,
        });
        return;
      }

      toast({
        title: t`Course published successfully`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });
    },
    onError: (error) => {
      let errorMessage = t`An error occurred while publishing the course.`;

      if (error instanceof TRPCClientError && error.message) {
        errorMessage = error.message;
      }

      const lines = errorMessage.split('\n');
      toast({
        title: t`Failed to publish course`,
        description: (
          <>
            {lines.map((line, i) => (
              <span key={line}>
                {line}
                {i < lines.length - 1 && <br />}
              </span>
            ))}
          </>
        ),
        status: 'error',
        isClosable: true,
        duration: 7000,
      });
    },
  });

  // Always a Publish button (the published state lives in the toolbar badge), so
  // a published course can be re-published after schedule/content changes. Match
  // the conversation editor's primary/sm action styling for toolbar parity.
  return (
    <Button
      variant="primary"
      size="sm"
      isLoading={publish.isPending || query.isLoading}
      onClick={() => {
        publish.mutate(courseId);
      }}
    >
      <Trans>Publish</Trans>
    </Button>
  );
}
